import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import * as workflowExecutionService from '../../services/workflowExecutionService';
import { 
  FiPlay, FiPause, FiCheck, FiX, FiClock, FiUsers, FiZap, 
  FiActivity, FiLayers, FiTarget, FiBarChart, FiDatabase,
  FiUser, FiTool, FiFileText, FiGitBranch, FiAlertCircle,
  FiSettings, FiCpu, FiMemoryStick
} from 'react-icons/fi';

interface WorkflowExecutionCardProps {
  execution: {
    id: string;
    workflow_id?: string;
    session_id?: string;
    status: 'active' | 'completed' | 'failed' | 'paused';
    created_at: string;
    updated_at?: string;
    end_time?: string;
    phases_count: number;
    phases_completed: number;
    current_phase_id?: string;
    phases?: Array<{
      id: string;
      type: string;
      status: string;
      start_time?: string;
      end_time?: string;
      artifacts?: any[];
      personas?: any[];
      tools?: any[];
      error_message?: string;
    }>;
    artifacts?: any[];
    total_tokens?: number;
    total_cost?: number;
    error_message?: string;
    metadata?: any;
  };
  compact?: boolean;
  className?: string;
}

export const WorkflowExecutionCard: React.FC<WorkflowExecutionCardProps> = ({ 
  execution, 
  compact = false,
  className = ''
}) => {
  const { darkMode } = useTheme();
  
  // State for lazy loading detailed execution data
  const [detailedExecution, setDetailedExecution] = useState<any>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [hasLoadedDetails, setHasLoadedDetails] = useState(false);

  // Lazy load detailed data if not available
  useEffect(() => {
    // Only load details if we don't have phase data and haven't loaded yet
    if (!execution.phases && !hasLoadedDetails && !isLoadingDetails) {
      setIsLoadingDetails(true);
      workflowExecutionService.getWorkflowExecution(execution.id)
        .then(detailedData => {
          setDetailedExecution(detailedData);
          setHasLoadedDetails(true);
        })
        .catch(error => {
          console.error('Failed to load detailed execution data:', error);
          setHasLoadedDetails(true); // Mark as loaded even on error to prevent retries
        })
        .finally(() => {
          setIsLoadingDetails(false);
        });
    }
  }, [execution.id, execution.phases, hasLoadedDetails, isLoadingDetails]);

  // Use the detailed data if available, otherwise fall back to basic data
  const executionData = detailedExecution || execution;

  const formatDuration = (durationMs: number) => {
    const days = Math.floor(durationMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((durationMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);
    
    return parts.join(' ');
  };

  const durationMs = execution.end_time 
    ? new Date(execution.end_time).getTime() - new Date(execution.created_at).getTime()
    : Date.now() - new Date(execution.created_at).getTime();

  const progressPercentage = execution.phases_count 
    ? Math.round((execution.phases_completed / execution.phases_count) * 100) 
    : 0;

  const getStatusIcon = () => {
    switch (execution.status) {
      case 'active': return <FiPlay className="w-4 h-4" />;
      case 'completed': return <FiCheck className="w-4 h-4" />;
      case 'failed': return <FiX className="w-4 h-4" />;
      case 'paused': return <FiPause className="w-4 h-4" />;
      default: return <FiClock className="w-4 h-4" />;
    }
  };

  const getStatusColor = () => {
    switch (execution.status) {
      case 'active': return 'text-blue-500 bg-blue-100 dark:bg-blue-900';
      case 'completed': return 'text-green-500 bg-green-100 dark:bg-green-900';
      case 'failed': return 'text-red-500 bg-red-100 dark:bg-red-900';
      case 'paused': return 'text-yellow-500 bg-yellow-100 dark:bg-yellow-900';
      default: return 'text-gray-500 bg-gray-100 dark:bg-gray-900';
    }
  };

  // Get current phase info
  const currentPhase = executionData.phases?.find(p => p.id === executionData.current_phase_id);
  const activePhases = executionData.phases?.filter(p => p.status === 'active' || p.status === 'in_progress') || [];
  const completedPhases = executionData.phases?.filter(p => p.status === 'completed') || [];
  const failedPhases = executionData.phases?.filter(p => p.status === 'failed') || [];

  // Calculate resource usage from detailed data
  const totalPersonas = executionData.phases?.reduce((sum, phase) => sum + (phase.config?.personas?.length || 0), 0) || 0;
  const totalTools = executionData.phases?.reduce((sum, phase) => sum + (phase.config?.tools?.length || 0), 0) || 0;
  const totalArtifacts = executionData.artifacts?.length || 
    executionData.phases?.reduce((sum, phase) => sum + (phase.artifacts?.length || 0), 0) || 0;

  const hasDetailedData = executionData.phases && executionData.phases.length > 0;

  return (
    <div className={`
      ${compact ? 'p-4 w-96' : 'p-5 w-[480px]'} rounded-xl shadow-2xl border-2 max-h-[600px] overflow-y-auto
      ${darkMode 
        ? 'bg-gray-800 border-gray-600 text-gray-100' 
        : 'bg-white border-gray-300 text-gray-900'
      }
      ${className}
      relative z-[9999] backdrop-blur-sm
    `}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <div className={`p-1.5 rounded-full ${getStatusColor()}`}>
              {getStatusIcon()}
            </div>
            <h3 className={`${compact ? 'text-sm' : 'text-base'} font-semibold truncate`}>
              Execution {execution.id.substring(0, 8)}...
            </h3>
          </div>
          <div className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'} space-y-1`}>
            <div>Session: {execution.session_id ? execution.session_id.substring(0, 8) + '...' : 'N/A'}</div>
            <div>Workflow: {execution.workflow_id ? execution.workflow_id.substring(0, 8) + '...' : 'N/A'}</div>
          </div>
        </div>
        
        <div className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor()}`}>
          {execution.status}
        </div>
      </div>

      {/* Error Message (if failed) */}
      {(executionData.error_message || execution.error_message) && (
        <div className={`mb-4 p-3 rounded-lg border-l-4 ${
          darkMode ? 'bg-red-900/20 border-red-500 text-red-300' : 'bg-red-50 border-red-500 text-red-700'
        }`}>
          <div className="flex items-start space-x-2">
            <FiAlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium text-xs">Error</div>
              <div className="text-xs mt-1">{executionData.error_message || execution.error_message}</div>
            </div>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Progress</span>
          <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {execution.phases_completed}/{execution.phases_count} phases
          </span>
        </div>
        <div className={`w-full h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
          <div 
            className="h-2 rounded-full bg-blue-500 transition-all duration-300" 
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        <div className={`text-xs mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {progressPercentage}% complete
          {currentPhase && ` • Current: ${currentPhase.type}`}
        </div>
      </div>

      {/* Resource Summary */}
      <div className="mb-4">
        <div className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Resources</div>
        {isLoadingDetails ? (
          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} italic flex items-center space-x-2`}>
            <div className="animate-spin w-3 h-3 border border-gray-400 border-t-transparent rounded-full"></div>
            <span>Loading resource details...</span>
          </div>
        ) : hasDetailedData ? (
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div className="flex items-center space-x-1">
              <FiUsers className={`w-3 h-3 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`} />
              <div>
                <div className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{totalPersonas}</div>
                <div className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Personas</div>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <FiTool className={`w-3 h-3 ${darkMode ? 'text-green-400' : 'text-green-500'}`} />
              <div>
                <div className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{totalTools}</div>
                <div className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Tools</div>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <FiFileText className={`w-3 h-3 ${darkMode ? 'text-purple-400' : 'text-purple-500'}`} />
              <div>
                <div className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{totalArtifacts}</div>
                <div className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Artifacts</div>
              </div>
            </div>
          </div>
        ) : (
          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} italic`}>
            No detailed resource information available
          </div>
        )}
      </div>

      {/* Usage Stats */}
      {(executionData.total_tokens || executionData.total_cost || execution.total_tokens || execution.total_cost) && (
        <div className="mb-4">
          <div className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Usage</div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            {(executionData.total_tokens || execution.total_tokens) && (
              <div className="flex items-center space-x-1">
                <FiCpu className={`w-3 h-3 ${darkMode ? 'text-yellow-400' : 'text-yellow-500'}`} />
                <div>
                  <div className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                    {(executionData.total_tokens || execution.total_tokens).toLocaleString()}
                  </div>
                  <div className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Tokens</div>
                </div>
              </div>
            )}
            {(executionData.total_cost || execution.total_cost) && (
              <div className="flex items-center space-x-1">
                <FiBarChart className={`w-3 h-3 ${darkMode ? 'text-green-400' : 'text-green-500'}`} />
                <div>
                  <div className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                    ${(executionData.total_cost || execution.total_cost).toFixed(4)}
                  </div>
                  <div className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Cost</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Phase Status Breakdown */}
      {executionData.phases && executionData.phases.length > 0 && (
        <div className="mb-4">
          <div className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Phase Status</div>
          <div className="space-y-1">
            {completedPhases.length > 0 && (
              <div className="flex items-center justify-between text-xs">
                <span className={`flex items-center space-x-1 ${darkMode ? 'text-green-300' : 'text-green-600'}`}>
                  <FiCheck className="w-3 h-3" />
                  <span>Completed</span>
                </span>
                <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{completedPhases.length}</span>
              </div>
            )}
            {activePhases.length > 0 && (
              <div className="flex items-center justify-between text-xs">
                <span className={`flex items-center space-x-1 ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                  <FiPlay className="w-3 h-3" />
                  <span>Active</span>
                </span>
                <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{activePhases.length}</span>
              </div>
            )}
            {failedPhases.length > 0 && (
              <div className="flex items-center justify-between text-xs">
                <span className={`flex items-center space-x-1 ${darkMode ? 'text-red-300' : 'text-red-600'}`}>
                  <FiX className="w-3 h-3" />
                  <span>Failed</span>
                </span>
                <span className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{failedPhases.length}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Timing Information */}
      <div className="mb-4">
        <div className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Timing</div>
        <div className="space-y-2 text-xs">
          <div className="flex items-center space-x-2">
            <FiClock className={`w-3 h-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            <div className="flex-1">
              <div className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Duration</div>
              <div className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{formatDuration(durationMs)}</div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <FiActivity className={`w-3 h-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            <div className="flex-1">
              <div className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Started</div>
              <div className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {new Date(execution.created_at).toLocaleString('en-US', { 
                  month: 'short', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit'
                })}
              </div>
            </div>
          </div>

          {(executionData.updated_at || execution.updated_at) && (
            <div className="flex items-center space-x-2">
              <FiSettings className={`w-3 h-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              <div className="flex-1">
                <div className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Last Updated</div>
                <div className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {new Date(executionData.updated_at || execution.updated_at).toLocaleString('en-US', { 
                    month: 'short', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  })}
                </div>
              </div>
            </div>
          )}

          {(executionData.end_time || execution.end_time) && (
            <div className="flex items-center space-x-2">
              <FiCheck className="w-3 h-3 text-green-500" />
              <div className="flex-1">
                <div className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Completed</div>
                <div className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {new Date(executionData.end_time || execution.end_time).toLocaleString('en-US', { 
                    month: 'short', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Metadata (if available) */}
      {((executionData.metadata && Object.keys(executionData.metadata).length > 0) || 
        (execution.metadata && Object.keys(execution.metadata).length > 0)) && (
        <div className={`pt-3 border-t ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
          <div className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Metadata</div>
          <div className="space-y-1 text-xs">
            {Object.entries(executionData.metadata || execution.metadata || {}).slice(0, 3).map(([key, value]) => (
              <div key={key} className="flex justify-between">
                <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} capitalize`}>
                  {key.replace(/_/g, ' ')}:
                </span>
                <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} truncate ml-2 max-w-32`}>
                  {typeof value === 'object' ? JSON.stringify(value).substring(0, 20) + '...' : String(value)}
                </span>
              </div>
            ))}
            {Object.keys(executionData.metadata || execution.metadata || {}).length > 3 && (
              <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} italic`}>
                +{Object.keys(executionData.metadata || execution.metadata || {}).length - 3} more fields...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowExecutionCard;
